import { has, includes, isArray, isEmpty, get } from 'lodash';
import { createAction } from 'redux-actions';
import { callApi, getEndpoint } from '../../../utils/api';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import { generateAutoSell } from '../../../utils/submission/submission-utility';
import { analyticsDataLayer } from '../../utils/analytics-helper';

const confirmValidation = (validationResults) => {
  if (isEmpty(validationResults)) {
    return true;
  }

  if (
    validationResults.transactions &&
    isEmpty(validationResults.transactions)
  ) {
    return true;
  }
  const validRedemptions = validationResults.transactions.filter(
    (transaction) => {
      return transaction.valid;
    }
  );
  return validRedemptions.length > 0;
};

export const getConfirmationNumber = (transactions) => {
  return isArray(transactions) &&
    transactions.length > 0 &&
    transactions[transactions.length - 1].confirmationNumber
    ? transactions[transactions.length - 1].confirmationNumber
    : '';
};

/**
 *
 * @param transactions - this is returned in the body of the execute purchase POST
 * @returns {String} this will be an empty string in the absence of a trade date, if event of trade date this
 * will return a formated string in the shape of 01/01/2018
 */
export const getTradeDate = (transactions) => {
  return isArray(transactions) &&
    transactions.length > 0 &&
    transactions[0].tradeDate
    ? formatUnixDate(transactions[0].tradeDate)
    : '';
};

export const retrieveErrorMessage = (payload) => {
  if (!isArray(payload)) {
    return '';
  }
  if (
    !payload.length &&
    !payload[0].transactions &&
    !payload[0].transactions.length
  ) {
    return '';
  }

  const validationMessages = get(payload[0].transactions[0], 'messages', [
    'transaction error',
  ]);
  return validationMessages.length
    ? validationMessages[0]
    : 'transaction error';
};

const VALIDATE_REDEMPTION = 'VALIDATE_REDEMPTION';
const GET_ELIGIBLE_REDDEM_TRADE_DATE = 'GET_ELIGIBLE_REDDEM_TRADE_DATE';
export const VALIDATE_DTS_REDEMPTION = 'VALIDATE_DTS_REDEMPTION';
const EXECUTE_REDEMPTION = 'EXECUTE_REDEMPTION';
export const EXECUTE_DTS_REDEMPTION = 'EXECUTE_DTS_REDEMPTION';
const REDEMPTION_RESULT = 'REDEMPTION_RESULT';
const REDEMPTION_VALIDATION = 'REDEMPTION_VALIDATION';
const EXECUTE_RECURRING_SELL = 'EXECUTE_RECURRING_SELL';

const hasTimedOut = (payload) => {
  try {
    return (
      has(payload, 'text') &&
      includes(payload.text, '{') &&
      JSON.parse(payload.text).error === 'timeout'
    );
  } catch (e) {
    return false;
  }
};

export default function executeRedemption(
  state = {
    redemption: {
      confirmationNumber: '',
      error: null,
      singleRedemptionFailed: false,
      singleRedemptionIsLoading: false,
      hasFailed: false,
      keepValidating: true,
      successfulTransactions: [],
      tradeDate: '',
      paperlessUpdate: null,
      redemptionResultFailed: false,
      redemptionResultLoading: false,
      redemptionResultSuccess: false,
      redemptionResultErrorMessage: {
        message: null,
      },

      redemptionTradeDates: {},
      redemptiontradeDatesFailed: false,
      redemptiontradeDatesLoading: false,
      redemptiontradeDatesSuccess: false,
    },
    validation: {
      isValidRedemption: true,
      validationMessage: {
        message: null,
      },
      validationResultLoaded: false,
      validationResultLoading: false,
      validationResults: [],
    },
    autoSellLoaded: false,
    autoSellLoading: false,
    autoSellFailed: false,
    autoSomeFailed: false,
    autoAllFailed: false,
    autoSellTransactions: [],
  },
  { payload, type }
) {
  switch (type) {
    case `${EXECUTE_REDEMPTION}_PENDING`:
    case `${EXECUTE_DTS_REDEMPTION}_PENDING`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          singleRedemptionIsLoading: true,
        },
      };
    case `${EXECUTE_REDEMPTION}_FULFILLED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          singleRedemptionIsLoading: false,
          singleRedemptionFailed: false,
          successfulTransactions: [
            ...state.redemption.successfulTransactions,
            payload.body.transactions[0],
          ],
        },
      };
    case `${EXECUTE_DTS_REDEMPTION}_FULFILLED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          singleRedemptionIsLoading: false,
          singleRedemptionFailed: false,
          successfulTransactions: [
            ...state.redemption.successfulTransactions,
            payload.body.transactions[0],
          ],
        },
      };
    case `${EXECUTE_REDEMPTION}_REJECTED`:
    case `${EXECUTE_DTS_REDEMPTION}_REJECTED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          singleRedemptionIsLoading: false,
          singleRedemptionFailed: true,
          timeout: hasTimedOut(payload),
          error: payload.body,
        },
      };
    case `${REDEMPTION_RESULT}_PENDING`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptionResultLoading: true,
          redemptionResultSuccess: false,
          successfulTransactions: [],
        },
      };
    case `${REDEMPTION_RESULT}_FULFILLED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptionResultLoading: false,
          redemptionResultSuccess: true,
          confirmationNumber: getConfirmationNumber(
            state.redemption.successfulTransactions
          ),
          tradeDate: getTradeDate(state.redemption.successfulTransactions),
        },
      };

    case `${REDEMPTION_RESULT}_REJECTED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptionResultLoading: false,
          timeout: hasTimedOut(payload),
          redemptionResultFailed: true,
          redemptionResultErrorMessage: {
            message: 'An error has been recognized in the current transaction',
          },
        },
      };
    case `${VALIDATE_REDEMPTION}_PENDING`:
    case `${VALIDATE_DTS_REDEMPTION}_PENDING`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationLoading: true,
        },
      };
    case `${VALIDATE_REDEMPTION}_FULFILLED`:
    case `${VALIDATE_DTS_REDEMPTION}_FULFILLED`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationResults: [
            ...state.validation.validationResults,
            payload.body,
          ],
          isValidRedemption: state.validation.isValidRedemption
            ? confirmValidation(payload.body)
            : false,
          validationLoading: false,
          keepValidating: true,
        },
      };
    case `${VALIDATE_REDEMPTION}_REJECTED`:
    case `${VALIDATE_DTS_REDEMPTION}_REJECTED`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationLoading: false,
          isValidRedemption: false,
          validationResults: null,
          keepValidating: false,
        },
      };
    case `${REDEMPTION_VALIDATION}_PENDING`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationResultLoaded: false,
          validationResultLoading: true,
          validationResults: [],
          isValidRedemption: true,
        },
      };
    case `${REDEMPTION_VALIDATION}_FULFILLED`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationResultLoaded: true,
          validationResultLoading: false,
        },
      };
    case `${REDEMPTION_VALIDATION}_REJECTED`:
      // there is a case where we have to return null because IAS fails and actually swallows the result ending in a 500 with no payload body the below validation null check handles that
      return {
        ...state,
        validation: {
          ...state.validation,
          validationResultLoaded: true,
          validationResultLoading: false,
          validationMessage: {
            message:
              state.validation.validationResults !== null
                ? retrieveErrorMessage(state.validation.validationResults)
                : 'An error has been recognized in the current transaction',
          },
        },
      };
    case `${GET_ELIGIBLE_REDDEM_TRADE_DATE}_PENDING`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptiontradeDatesFailed: false,
          redemptiontradeDatesLoading: true,
          redemptiontradeDatesSuccess: false,
        },
      };
    case `${GET_ELIGIBLE_REDDEM_TRADE_DATE}_FULFILLED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptionTradeDates: {
            ...state.redemption.redemptionTradeDates,
            ...payload.body.estimatedTradeDate,
          },
          redemptiontradeDatesFailed: false,
          redemptiontradeDatesLoading: false,
          redemptiontradeDatesSuccess: true,
        },
      };

    case `${GET_ELIGIBLE_REDDEM_TRADE_DATE}_REJECTED`:
      return {
        ...state,
        redemption: {
          ...state.redemption,
          redemptiontradeDatesFailed: true,
          redemptiontradeDatesLoading: false,
          redemptiontradeDatesSuccess: false,
        },
      };
    case `${EXECUTE_RECURRING_SELL}_PENDING`:
      return {
        ...state,
        autoSellLoaded: false,
        autoSellLoading: true,
        autoSellFailed: false,
      };
    case `${EXECUTE_RECURRING_SELL}_FULFILLED`: {
      const someFailed = payload.body.transactions.some(
        (transaction) => !transaction.success
      );
      const allFailed = payload.body.transactions.every(
        (transaction) => !transaction.success
      );
      return {
        ...state,
        autoSellLoaded: true,
        autoSellLoading: false,
        autoSellFailed: false,
        autoSomeFailed: someFailed,
        autoAllFailed: allFailed,
        autoSellTransactions: payload.body.transactions,
      };
    }
    case `${EXECUTE_RECURRING_SELL}_REJECTED`:
      return {
        ...state,
        autoSellLoaded: true,
        autoSellLoading: false,
        autoSellFailed: true,
      };
    default:
      return state;
  }
}

export const sendRedemption = (finalPayload) => {
  return () => {
    const action = createAction(EXECUTE_REDEMPTION)(
      callApi(getEndpoint('finalRedemption'), { method: 'POST' }, finalPayload)
    );
    return action;
  };
};

export const sendDtsRedemption = (finalPayload) => {
  return () => {
    const action = createAction(EXECUTE_DTS_REDEMPTION)(
      callApi(
        getEndpoint('finalDtsRedemption'),
        { method: 'POST' },
        finalPayload
      )
    );
    return action;
  };
};

export const setRedemptionResultStart = () => {
  const action = createAction(`${REDEMPTION_RESULT}_PENDING`)(true);
  return (dispatch) => {
    dispatch(action);
  };
};

export const setRedemptionResultFailed = (msg = '') => {
  const action = createAction(`${REDEMPTION_RESULT}_REJECTED`)(msg);
  return (dispatch) => {
    dispatch(action);
  };
};

export const setRedemptionResultSuccessful = () => {
  const action = createAction(`${REDEMPTION_RESULT}_FULFILLED`)(true);
  return (dispatch) => {
    dispatch(action);
  };
};

export const clearAARCache = () => {
  const action = createAction('CLEAR_AAR_CACHE')(
    callApi(getEndpoint('aarCacheClear'), { method: 'POST' }, true)
  );
  return (dispatch) => {
    dispatch(action);
  };
};

/**
 *
 * @param redemptionPayload takes in a possible redemption payload in the format of
 * {
	"bank": {
		"bankAgreementID": "x"
	},
	"unit": {
			"code": "D",
			"enumerationID": Number,
			"enumerationName": String
	},
	"delivery": {
			"enumerationID": Number,
			"enumerationName": String
	},
	"sourceAgreement": {
			"agreementID": String
	},
	"amount": Number
}
 * @returns {function(*)} dispactches action
 */
export const validateSingleRedemption = (redemptionPayload) => {
  return () => {
    const action = createAction(VALIDATE_REDEMPTION)(
      callApi(
        getEndpoint('validateRedemption'),
        { method: 'POST' },
        redemptionPayload
      )
    );
    return action;
  };
};

export const getEligibleReddemTradeDates = (payload) => {
  return () => {
    const action = createAction(GET_ELIGIBLE_REDDEM_TRADE_DATE)(
      callApi(getEndpoint('redeemTradeDates'), { method: 'POST' }, payload)
    );
    return action;
  };
};

export const validateDtsRedemption = (redemptionDtsPayload) => {
  return () => {
    return createAction(VALIDATE_DTS_REDEMPTION)(
      callApi(
        getEndpoint('validateDtsRedemption'),
        { method: 'POST' },
        redemptionDtsPayload
      )
    );
  };
};

export const setValidationStart = () => {
  const action = createAction(`${REDEMPTION_VALIDATION}_PENDING`)(true);
  return (dispatch) => {
    dispatch(action);
  };
};

export const setValidationFailed = (msg = '') => {
  const action = createAction(`${REDEMPTION_VALIDATION}_REJECTED`)(msg);
  return (dispatch) => {
    dispatch(action);
  };
};

export const setValidationSuccessful = () => {
  const action = createAction(`${REDEMPTION_VALIDATION}_FULFILLED`)(true);
  return (dispatch) => {
    dispatch(action);
  };
};

export const executeAutomaticSell = ({
  selectedFunds,
  selectedDeliveryMethod,
  selectedBank,
  userAge,
}) => {
  const payload = generateAutoSell(
    selectedFunds,
    selectedDeliveryMethod,
    selectedBank,
    userAge
  );
  try {
    const getAnalyticsDataForTransaction = (payload) => {
      const { systematicTransactions } = payload;
      const purchaseAmounts = systematicTransactions.map((target) => {
        return target.amount;
      });

      return {
        amount: purchaseAmounts,
      };
    };
    analyticsDataLayer({
      accountTypes: ['MUTUAL_FUND'],
      ...getAnalyticsDataForTransaction(payload),
      quantity: selectedFunds.length, // only allow single recurring exchange
    });
  } catch (e) {
    // don't let analytics errors cause other things to fail
  }
  return (dispatch) => {
    const action = createAction(EXECUTE_RECURRING_SELL)(
      callApi(getEndpoint('recurringPurchase'), { method: 'POST' }, payload)
    );
    dispatch(action);
  };
};
